import { TreeSelect } from "antd";
import type { DefaultOptionType } from "antd/es/select";
import { useMemo } from "react";
import { useTerritories } from "../hooks/useTerritories";
import type { Territory } from "../services/territory.service";

export interface TerritorySelectProps {
	organizationId?: string;
	value?: string | string[];
	onChange?: (value: string | string[]) => void;
	multiple?: boolean;
	placeholder?: string;
	allowClear?: boolean;
	disabled?: boolean;
	excludeId?: string;
	className?: string;
	style?: React.CSSProperties;
}

interface TreeNode extends DefaultOptionType {
	key: string;
	value: string;
	title: string;
	children?: TreeNode[];
	disabled?: boolean;
}

function getExcludedIds(items: Territory[], excludeId?: string): Set<string> {
	const excluded = new Set<string>();
	if (!excludeId) {
		return excluded;
	}
	excluded.add(excludeId);
	let added = true;
	while (added) {
		added = false;
		for (const item of items) {
			if (item.parentId && excluded.has(item.parentId) && !excluded.has(item.id)) {
				excluded.add(item.id);
				added = true;
			}
		}
	}
	return excluded;
}

function pruneEmptyChildren(nodes: TreeNode[]) {
	for (const node of nodes) {
		if (node.children) {
			if (node.children.length === 0) {
				delete node.children;
			} else {
				pruneEmptyChildren(node.children);
			}
		}
	}
}

function buildTerritoryTree(items: Territory[], excludeId?: string): TreeNode[] {
	const excludedSet = getExcludedIds(items, excludeId);
	const allowedItems = items.filter((item) => !excludedSet.has(item.id));
	const itemMap = new Map<string, TreeNode>();

	for (const item of allowedItems) {
		itemMap.set(item.id, {
			key: item.id,
			value: item.id,
			title: item.name,
			children: [],
		});
	}

	const rootNodes: TreeNode[] = [];

	for (const item of allowedItems) {
		const node = itemMap.get(item.id);
		if (!node) {
			continue;
		}

		if (item.parentId && itemMap.has(item.parentId)) {
			itemMap.get(item.parentId)?.children?.push(node);
		} else {
			rootNodes.push(node);
		}
	}

	pruneEmptyChildren(rootNodes);
	return rootNodes;
}

export function TerritorySelect({
	organizationId,
	value,
	onChange,
	multiple = false,
	placeholder = "Hududni tanlang",
	allowClear = true,
	disabled = false,
	excludeId,
	className,
	style,
}: TerritorySelectProps) {
	const { data, isLoading } = useTerritories(
		{
			organizationId: organizationId || "",
			limit: 1000,
			flat: true,
		},
		{ enabled: !!organizationId }
	);

	const treeData = useMemo(() => {
		const items = data?.data?.items ?? [];
		return buildTerritoryTree(items, excludeId);
	}, [data?.data?.items, excludeId]);

	return (
		<TreeSelect
			treeData={treeData}
			value={value}
			onChange={onChange}
			multiple={multiple}
			treeCheckable={multiple}
			showCheckedStrategy={TreeSelect.SHOW_ALL}
			placeholder={placeholder}
			allowClear={allowClear}
			disabled={disabled}
			loading={isLoading}
			showSearch
			treeNodeFilterProp="title"
			treeDefaultExpandAll
			treeLine={{ showLeafIcon: false }}
			className={className}
			style={{ width: "100%", ...style }}
			dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
		/>
	);
}
